import { Injectable, NgZone } from '@angular/core';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { AppService } from './app.service';

/**
 * Author: Aldrin
 * Description: To detect time drift in users local machine.
 * Created On: 2026-Jan-30
 */

@Injectable({
  providedIn: 'root'
})
export class TimeIntegrityService {

  private readonly CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
  private readonly WARNING_DRIFT_MIN = 1;
  private readonly SEVERE_DRIFT_MIN = 5;

  private checkSub?: Subscription;

  constructor(
    private readonly ngZone: NgZone,
    private readonly appService: AppService
  ) { }

  private readonly stop$ = new Subject<void>();

  startSilentChecks(onSevereDrift: (type: string) => void): void {
    this.stopSilentChecks();

    // Run outside Angular to avoid unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      this.checkSub = interval(this.CHECK_INTERVAL_MS).pipe(takeUntil(this.stop$)).subscribe(() => {
        this.checkTime(onSevereDrift);
      });
    });

    // Immediate first check
    this.checkTime(onSevereDrift);
  }

  stopSilentChecks(): void {
    this.stop$.next();
    this.checkSub?.unsubscribe();
  }

  private checkTime(onSevereDrift: (string: any) => void): void {
    const clientNow = new Date();

    this.appService.getUtcTime(true).subscribe({
      next: (res: any) => {

        const serverUtc = new Date(res.serverUtc);
        const diffMin = Math.abs(clientNow.getTime() - serverUtc.getTime()) / (1000 * 60);

        if (diffMin > this.SEVERE_DRIFT_MIN) {
          console.error('[TIME] Clock drift detected:')
          onSevereDrift('severe');
        } else if (diffMin > this.WARNING_DRIFT_MIN) {
          console.warn('[TIME] Clock drift detected:', diffMin.toFixed(2), 'minutes');
          onSevereDrift('warning + ' + diffMin.toFixed(2));
        }
      },
      error: () => {
        // No alert – network failure ≠ time failure
        console.warn('[TIME] Unable to verify server time');
      }
    });
  }
}
